import { useState } from 'react'
import { Card } from '../../../shared/components/Card'
import { PublishStatusBadge } from './PublishStatusBadge'
import { ProductTypeBadge } from './ProductTypeBadge'
import { StockStatusBadge } from './StockStatusBadge'
import { useCurrencyFormatter } from '../../../shared/utils/currencyUtils'
import type { StockStatus } from '../utils/stockStatus'

interface ProductCardProps {
  id: string
  name: string
  description: string
  basePrice: number
  sellingPrice: number
  image?: string
  publishStatus: 'Published' | 'Draft'
  productType: string
  /** PE-2169 — computed by productStockStatus() from the variant leaves */
  inStock: StockStatus
  stockQuantity?: number
  sizes: string[]
  colors: string[]
  onView: (id: string) => void
  /** Optional - if not provided, Edit button will be hidden */
  onEdit?: (id: string) => void
  /** Optional - if not provided, Delete button will be hidden */
  onDelete?: (id: string) => void
  /** Optional - if not provided, Publish button will be hidden */
  onPublish?: (id: string) => void
  /** when true, show a small loading indicator in place of the delete icon */
  isDeleting?: boolean
  /** when true, show a small loading indicator for publish action */
  isPublishing?: boolean
  merchantName?: string
}

export const ProductCard = ({
  id,
  name,
  description,
  basePrice,
  sellingPrice,
  image,
  publishStatus,
  productType,
  inStock,
  stockQuantity,
  sizes,
  colors,
  onView,
  onEdit,
  onDelete,
  isDeleting,
  merchantName
}: ProductCardProps) => {
  const [imageError, setImageError] = useState(false)
  const { formatPrice } = useCurrencyFormatter()


  const handleImageError = () => {
    setImageError(true)
  }

  const handleImageLoad = () => {
    setImageError(false)
  }

  const showFallback = !image || imageError

  const isDiscounted = sellingPrice > 0 && basePrice > 0 && sellingPrice < basePrice
  const discountPct = isDiscounted
    ? Math.round(((basePrice - sellingPrice) / basePrice) * 100)
    : 0

  return (
    <Card className="p-0 overflow-hidden w-[333px] h-[720px] rounded-[10px] border-[1px] border-[#B8B5B6]">
      {/* Image Section */}
      <div className="flex items-center justify-center w-[332.9px] h-[388px] bg-muted">
        {showFallback ? (
          <div className="flex flex-col items-center justify-center text-muted-foreground p-4">
            <div className="w-16 h-16 mb-3 bg-muted rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
            </div>
            <span className="text-sm font-medium">No Image</span>
          </div>
        ) : (
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        )}
      </div>

      {/* Content Section */}
      <div className="p-2 py-8">
        {/* Header with name and status */}
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="font-[600] text-[16px] text-foreground">
              {name ? (name.length > 20 ? `${name.slice(0, 15)}...` : name) : 'Untitled Product'}
            </h3>
            {merchantName && (
              <span className="text-[12px] text-muted-foreground font-medium block mt-1">
                By {merchantName}
              </span>
            )}
          </div>
          <PublishStatusBadge status={publishStatus} />
        </div>

        {/* Description */}
        <p className="font-[400] text-[13px] text-foreground my-6">
          {description ?
            (description.length > 90 ? `${description.slice(0, 90)}...` : description) :
            'No description available'
          }
        </p>

        {/* Price and Product Type */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col gap-0.5">
            {isDiscounted ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="font-[600] text-[16px] text-foreground">
                    {formatPrice(sellingPrice)}
                  </span>
                  <span
                    className="px-2 py-0.5 text-xs font-[600] rounded-[10px] bg-[#FECDCD] text-[#AB0303]"
                    aria-label={`${discountPct}% off`}
                  >
                    -{discountPct}%
                  </span>
                </div>
                <span className="text-[13px] text-muted-foreground line-through">
                  {formatPrice(basePrice)}
                </span>
              </>
            ) : (
              <span className="font-[600] text-[16px] text-foreground">
                {formatPrice(basePrice)}
              </span>
            )}
          </div>
          <ProductTypeBadge type={productType || 'Unknown'} />
        </div>

        {/* Size and Colors */}
        <div className="flex justify-between items-center mb-4">
          <div className='font-[400] text-[14px] text-foreground'>
            <span className="">Size: </span>
            <span className="font-[600]">
              {sizes && sizes.length > 0 ? sizes.join(', ') : 'N/A'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-[400] text-[14px] text-foreground">Colors:</span>
            <div className="flex gap-1">
              {colors && colors.length > 0 ? (
                colors.map((color, index) => (
                  <div
                    key={index}
                    className="w-4 h-4 rounded-full border border-[#B8B5B6]"
                    style={{ backgroundColor: color || '#ccc' }}
                  />
                ))
              ) : (
                <span className="text-xs text-muted-foreground">N/A</span>
              )}
            </div>
          </div>
        </div>

        {/* Stock Status */}
        <div className="mb-4">
          <StockStatusBadge inStock={inStock} quantity={stockQuantity || 0} />
        </div>

        {/* Action Buttons */}
        <div className="w-full flex justify-between gap-1">
          <button
            onClick={() => onView(id)}
            className=" px-4 py-2 border border-[#B8B5B6] rounded-[10px] w-[116px] h-[32px] text-[14px] font-[600] text-foreground hover:bg-muted flex items-center justify-center gap-2"
          >
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_2808_26280)">
                <path d="M1.16406 8.00002C1.16406 8.00002 3.83073 2.66669 8.4974 2.66669C13.1641 2.66669 15.8307 8.00002 15.8307 8.00002C15.8307 8.00002 13.1641 13.3334 8.4974 13.3334C3.83073 13.3334 1.16406 8.00002 1.16406 8.00002Z" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M1.16406 8.00002C1.16406 8.00002 3.83073 2.66669 8.4974 2.66669C13.1641 2.66669 15.8307 8.00002 15.8307 8.00002C15.8307 8.00002 13.1641 13.3334 8.4974 13.3334C3.83073 13.3334 1.16406 8.00002 1.16406 8.00002Z" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M1.16406 8.00002C1.16406 8.00002 3.83073 2.66669 8.4974 2.66669C13.1641 2.66669 15.8307 8.00002 15.8307 8.00002C15.8307 8.00002 13.1641 13.3334 8.4974 13.3334C3.83073 13.3334 1.16406 8.00002 1.16406 8.00002Z" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M8.5 10C9.60457 10 10.5 9.10457 10.5 8C10.5 6.89543 9.60457 6 8.5 6C7.39543 6 6.5 6.89543 6.5 8C6.5 9.10457 7.39543 10 8.5 10Z" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M8.5 10C9.60457 10 10.5 9.10457 10.5 8C10.5 6.89543 9.60457 6 8.5 6C7.39543 6 6.5 6.89543 6.5 8C6.5 9.10457 7.39543 10 8.5 10Z" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M8.5 10C9.60457 10 10.5 9.10457 10.5 8C10.5 6.89543 9.60457 6 8.5 6C7.39543 6 6.5 6.89543 6.5 8C6.5 9.10457 7.39543 10 8.5 10Z" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_2808_26280">
                  <rect width="16" height="16" fill="white" transform="translate(0.5)" />
                </clipPath>
              </defs>
            </svg>

            View
          </button>
          {onEdit && (
            <button
              onClick={() => onEdit(id)}
              className=" px-4 py-2 border border-[#B8B5B6] rounded-[10px] w-[116px] h-[32px] text-[14px] font-[600] text-foreground hover:bg-muted flex items-center justify-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_2808_26285)">
                  <path d="M7.33594 2.66669H2.66927C2.31565 2.66669 1.97651 2.80716 1.72646 3.05721C1.47641 3.30726 1.33594 3.6464 1.33594 4.00002V13.3334C1.33594 13.687 1.47641 14.0261 1.72646 14.2762C1.97651 14.5262 2.31565 14.6667 2.66927 14.6667H12.0026C12.3562 14.6667 12.6954 14.5262 12.9454 14.2762C13.1955 14.0261 13.3359 13.687 13.3359 13.3334V8.66669" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M7.33594 2.66669H2.66927C2.31565 2.66669 1.97651 2.80716 1.72646 3.05721C1.47641 3.30726 1.33594 3.6464 1.33594 4.00002V13.3334C1.33594 13.687 1.47641 14.0261 1.72646 14.2762C1.97651 14.5262 2.31565 14.6667 2.66927 14.6667H12.0026C12.3562 14.6667 12.6954 14.5262 12.9454 14.2762C13.1955 14.0261 13.3359 13.687 13.3359 13.3334V8.66669" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M7.33594 2.66669H2.66927C2.31565 2.66669 1.97651 2.80716 1.72646 3.05721C1.47641 3.30726 1.33594 3.6464 1.33594 4.00002V13.3334C1.33594 13.687 1.47641 14.0261 1.72646 14.2762C1.97651 14.5262 2.31565 14.6667 2.66927 14.6667H12.0026C12.3562 14.6667 12.6954 14.5262 12.9454 14.2762C13.1955 14.0261 13.3359 13.687 13.3359 13.3334V8.66669" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M12.3359 1.66684C12.6012 1.40162 12.9609 1.25262 13.3359 1.25262C13.711 1.25262 14.0707 1.40162 14.3359 1.66684C14.6012 1.93205 14.7502 2.29177 14.7502 2.66684C14.7502 3.04191 14.6012 3.40162 14.3359 3.66684L8.0026 10.0002L5.33594 10.6668L6.0026 8.00017L12.3359 1.66684Z" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M12.3359 1.66684C12.6012 1.40162 12.9609 1.25262 13.3359 1.25262C13.711 1.25262 14.0707 1.40162 14.3359 1.66684C14.6012 1.93205 14.7502 2.29177 14.7502 2.66684C14.7502 3.04191 14.6012 3.40162 14.3359 3.66684L8.0026 10.0002L5.33594 10.6668L6.0026 8.00017L12.3359 1.66684Z" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M12.3359 1.66684C12.6012 1.40162 12.9609 1.25262 13.3359 1.25262C13.711 1.25262 14.0707 1.40162 14.3359 1.66684C14.6012 1.93205 14.7502 2.29177 14.7502 2.66684C14.7502 3.04191 14.6012 3.40162 14.3359 3.66684L8.0026 10.0002L5.33594 10.6668L6.0026 8.00017L12.3359 1.66684Z" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_2808_26285">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>

              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(id)}
              disabled={!!(typeof isDeleting !== 'undefined' && isDeleting)}
              className={` px-2 py-2 border border-[#B8B5B6] rounded-[10px] w-[36px] h-[32px] text-[14px] font-[600] text-foreground hover:bg-muted flex items-center justify-center ${isDeleting ? 'cursor-not-allowed opacity-60' : ''
                }`}
            >
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 4H3.33333H14" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M2 4H3.33333H14" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M2 4H3.33333H14" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12.6693 3.99998V13.3333C12.6693 13.6869 12.5288 14.0261 12.2787 14.2761C12.0287 14.5262 11.6896 14.6666 11.3359 14.6666H4.66927C4.31565 14.6666 3.97651 14.5262 3.72646 14.2761C3.47641 14.0261 3.33594 13.6869 3.33594 13.3333V3.99998M5.33594 3.99998V2.66665C5.33594 2.31302 5.47641 1.97389 5.72646 1.72384C5.97651 1.47379 6.31565 1.33331 6.66927 1.33331H9.33594C9.68956 1.33331 10.0287 1.47379 10.2787 1.72384C10.5288 1.97389 10.6693 2.31302 10.6693 2.66665V3.99998" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12.6693 3.99998V13.3333C12.6693 13.6869 12.5288 14.0261 12.2787 14.2761C12.0287 14.5262 11.6896 14.6666 11.3359 14.6666H4.66927C4.31565 14.6666 3.97651 14.5262 3.72646 14.2761C3.47641 14.0261 3.33594 13.6869 3.33594 13.3333V3.99998M5.33594 3.99998V2.66665C5.33594 2.31302 5.47641 1.97389 5.72646 1.72384C5.97651 1.47379 6.31565 1.33331 6.66927 1.33331H9.33594C9.68956 1.33331 10.0287 1.47379 10.2787 1.72384C10.5288 1.97389 10.6693 2.31302 10.6693 2.66665V3.99998" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12.6693 3.99998V13.3333C12.6693 13.6869 12.5288 14.0261 12.2787 14.2761C12.0287 14.5262 11.6896 14.6666 11.3359 14.6666H4.66927C4.31565 14.6666 3.97651 14.5262 3.72646 14.2761C3.47641 14.0261 3.33594 13.6869 3.33594 13.3333V3.99998M5.33594 3.99998V2.66665C5.33594 2.31302 5.47641 1.97389 5.72646 1.72384C5.97651 1.47379 6.31565 1.33331 6.66927 1.33331H9.33594C9.68956 1.33331 10.0287 1.47379 10.2787 1.72384C10.5288 1.97389 10.6693 2.31302 10.6693 2.66665V3.99998" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M6.66406 7.33331V11.3333" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M6.66406 7.33331V11.3333" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M6.66406 7.33331V11.3333" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.33594 7.33331V11.3333" stroke="#3F3E42" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.33594 7.33331V11.3333" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.33594 7.33331V11.3333" stroke="black" stroke-opacity="0.2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
              )}

            </button>
          )}
        </div>
      </div>
    </Card>
  )
}